import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from "@material-ui/core";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { toast } from "react-toastify";

import {
  listTagRoutes,
  createTagRoute,
  updateTagRoute,
  deleteTagRoute,
} from "../../services/agentIa";
import { listTags } from "../../services/tags";
import toastError from "../../errors/toastError";

const MODE_LABEL = {
  off: "Apagado",
  suggestion: "Solo sugerencia",
  autonomous: "Control libre (próximamente)",
};

const AgentIaRoutingModal = ({ open, onClose, agent }) => {
  const [allTags, setAllTags] = useState([]);
  const [allRoutes, setAllRoutes] = useState([]);
  const [selectedTagId, setSelectedTagId] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [tags, routes] = await Promise.all([listTags(), listTagRoutes()]);
      setAllTags(tags);
      setAllRoutes(routes);
    } catch (err) {
      toastError(err);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setSelectedTagId("");
      fetchData();
    }
  }, [open, fetchData]);

  if (!agent) return null;

  const myRoutes = allRoutes.filter((r) => r.agentIaAgentId === agent.id);
  const mappedTagIds = new Set(allRoutes.map((r) => r.tagId));
  const availableTags = allTags.filter((t) => !mappedTagIds.has(t.id));

  const handleAdd = async () => {
    if (!selectedTagId) return;
    setSaving(true);
    try {
      await createTagRoute({
        tagId: selectedTagId,
        agentIaAgentId: agent.id,
        mode: "suggestion",
      });
      setSelectedTagId("");
      fetchData();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleModeChange = async (route, mode) => {
    try {
      await updateTagRoute(route.id, { mode });
      fetchData();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDelete = async (route) => {
    try {
      await deleteTagRoute(route.id);
      fetchData();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Enrutamiento por etiqueta — {agent.name}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="caption" color="textSecondary" component="div" style={{ marginBottom: 16 }}>
          Los contactos con alguna de estas etiquetas reciben sugerencias de
          este agente en tickets reales. Una etiqueta solo puede apuntar a un
          agente a la vez.
        </Typography>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Etiqueta</TableCell>
              <TableCell>Modo</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {myRoutes.map((route) => (
              <TableRow key={route.id}>
                <TableCell>
                  <Chip
                    size="small"
                    label={route.tag?.name}
                    style={{
                      backgroundColor: route.tag?.color,
                      color: "#fff",
                    }}
                  />
                </TableCell>
                <TableCell>
                  <FormControl variant="outlined" size="small" fullWidth>
                    <Select
                      value={route.mode}
                      onChange={(e) => handleModeChange(route, e.target.value)}
                    >
                      <MenuItem value="off">{MODE_LABEL.off}</MenuItem>
                      <MenuItem value="suggestion">
                        {MODE_LABEL.suggestion}
                      </MenuItem>
                      <Tooltip title="Todavía no está disponible — primero validamos el modo sugerencia">
                        <span>
                          <MenuItem value="autonomous" disabled>
                            {MODE_LABEL.autonomous}
                          </MenuItem>
                        </span>
                      </Tooltip>
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => handleDelete(route)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {myRoutes.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  Este agente todavía no tiene etiquetas asignadas.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <div style={{ display: "flex", gap: 8, marginTop: 16, alignItems: "center" }}>
          <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
            <InputLabel>Agregar etiqueta</InputLabel>
            <Select
              label="Agregar etiqueta"
              value={selectedTagId}
              onChange={(e) => setSelectedTagId(e.target.value)}
            >
              {availableTags.map((tag) => (
                <MenuItem key={tag.id} value={tag.id}>
                  {tag.name}
                </MenuItem>
              ))}
              {availableTags.length === 0 && (
                <MenuItem value="" disabled>
                  No hay etiquetas libres
                </MenuItem>
              )}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            color="primary"
            disabled={!selectedTagId || saving}
            onClick={handleAdd}
          >
            Agregar
          </Button>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AgentIaRoutingModal;
